// Page-tool extensions: helpers injected into the proxied tab iframes. Proxied
// content is served under our own origin, so the iframe is same-origin and we can
// reach into its contentWindow/contentDocument. Applied on every proxied frame
// load (view.js) and re-applied live when a toggle changes. EVERYTHING here is
// best-effort and wrapped: a page that resists injection just doesn't get the
// tool - it never breaks the tab or the app.
import { frames } from "./view.js";
import { byId, active } from "./state.js";
import { get, subscribe } from "../shared/settings-store.js";

const ERUDA_URL = __RVM.eruda; // first-party eruda.js (build-cloaked); "" if unavailable
const DARK_STYLE_ID = "__rv-ext-dark";
// Invert the whole page, then re-invert media so photos/video look normal - the
// classic "force dark" trick. Deliberately !important so it wins over page CSS.
const DARK_CSS =
	"html{filter:invert(1) hue-rotate(180deg) !important;background:#111 !important;}" +
	"img,video,picture,canvas,svg,image,[style*='background-image'],iframe,embed,object{filter:invert(1) hue-rotate(180deg) !important;}";

function frameWin(id) {
	const f = frames.get(id);
	try { return f && f.contentWindow ? f.contentWindow : null; } catch (e) { return null; }
}
function isProxied(t) { return !!(t && t.src && !t.internal && !t.view && !t.error); }
// Sandbox mode: the page lives on the sandbox origin, where the engine host runs the same tools
// (view/engine-host.js); its frame stand-in here forwards the toolbar actions. The switches reach
// it with the other engine settings, so applyExtFrame/refreshAll find no window here and do nothing.
function remoteOf(t) { const f = t && frames.get(t.id); return f && f.rvRemote ? f : null; }

// --- DevTools (Eruda) -----------------------------------------------------
let erudaText = null;
async function erudaSource() {
	if (erudaText != null) return erudaText;
	if (!ERUDA_URL) throw new Error("eruda unavailable");
	const r = await fetch(ERUDA_URL, { cache: "force-cache" });
	if (!r.ok) throw new Error("eruda fetch failed");
	erudaText = await r.text();
	return erudaText;
}

async function enableDevtools(id) {
	const win = frameWin(id);
	if (!win || win.__rvEruda) return;
	let code;
	try { code = await erudaSource(); } catch (e) { return; }
	const w = frameWin(id); // re-check: the frame may have navigated while we awaited
	if (!w || w.__rvEruda) return;
	try {
		w.__rvEruda = true;
		const doc = w.document;
		// Inline <script> (textContent, not src) so the proxy's URL rewriting never
		// touches it - it runs verbatim in the proxied page's own context.
		const s = doc.createElement("script");
		s.textContent = code + "\n;try{if(window.eruda){eruda.init();eruda._rvReady=1;}}catch(e){}";
		(doc.head || doc.documentElement || doc.body).appendChild(s);
	} catch (e) { try { w.__rvEruda = false; } catch (e2) {} }
}
function disableDevtools(id) {
	const win = frameWin(id);
	if (!win || !win.__rvEruda) return;
	try { if (win.eruda) win.eruda.destroy(); } catch (e) {}
	try { win.__rvEruda = false; } catch (e) {}
}
// Toolbar action: open the DevTools panel on the active tab (turns the extension
// on for the session's frames if it wasn't already). Returns false if there's no
// proxied page to attach to.
export function openDevtoolsActive() {
	const t = active();
	if (!isProxied(t)) return false;
	if (remoteOf(t)) { remoteOf(t).tool("devtools"); return true; }
	const run = () => { const w = frameWin(t.id); try { if (w && w.eruda) { w.eruda.show(); return true; } } catch (e) {} return false; };
	if (run()) return true;
	enableDevtools(t.id).then(() => setTimeout(run, 200));
	return true;
}

// --- Dark web pages -------------------------------------------------------
// The toolbar button pauses it for this visit (a page that looks odd dark) without turning it off.
let darkPaused = false;
export const darkPausedNow = () => darkPaused;
export function setDarkPaused(v) { darkPaused = !!v; refreshAll(); }
function applyDark(id, on) {
	on = on && !darkPaused;
	const win = frameWin(id);
	if (!win) return;
	try {
		const doc = win.document;
		let st = doc.getElementById(DARK_STYLE_ID);
		if (on) {
			if (!st) { st = doc.createElement("style"); st.id = DARK_STYLE_ID; (doc.head || doc.documentElement).appendChild(st); }
			if (st.textContent !== DARK_CSS) st.textContent = DARK_CSS;
		} else if (st) { st.remove(); }
	} catch (e) {}
}

// --- Highlighter ----------------------------------------------------------
// When on, a mouseup with a non-collapsed selection wraps it in a highlight
// span. In-session only (a proxied reload re-fetches the page, losing spans).
// The toolbar button puts the pen down and picks it up again (pen mode), so selecting text to copy
// doesn't always highlight it.
let pen = true;
export const penOn = () => pen;
export function setPen(on) { pen = !!on; }
function highlight(win) {
	if (!pen) return;
	try {
		const sel = win.getSelection();
		if (!sel || sel.isCollapsed || !sel.rangeCount) return;
		const range = sel.getRangeAt(0);
		if (range.collapsed) return;
		const mark = win.document.createElement("span");
		mark.className = "__rv-hl";
		mark.setAttribute("style", "background:#ffe066;color:#000;border-radius:2px;padding:0 1px;");
		try { range.surroundContents(mark); }
		catch (e) { try { mark.appendChild(range.extractContents()); range.insertNode(mark); } catch (e2) { return; } }
		sel.removeAllRanges();
	} catch (e) {}
}
function applyHighlighter(id, on) {
	const win = frameWin(id);
	if (!win) return;
	try {
		if (on) {
			if (win.__rvHlFn) return;
			const fn = () => highlight(win);
			win.__rvHlFn = fn;
			win.document.addEventListener("mouseup", fn, true);
		} else if (win.__rvHlFn) {
			try { win.document.removeEventListener("mouseup", win.__rvHlFn, true); } catch (e) {}
			win.__rvHlFn = null;
		}
	} catch (e) {}
}
// Toolbar action: unwrap every highlight on the active page.
export function clearHighlightsActive() {
	const t = active();
	if (!isProxied(t)) return { ok: false, reason: "Open a page first." };
	if (remoteOf(t)) { remoteOf(t).tool("clearHighlights"); return { ok: true }; }
	const win = frameWin(t.id);
	if (!win) return { ok: false };
	try {
		const marks = win.document.querySelectorAll(".__rv-hl");
		marks.forEach((m) => { const parent = m.parentNode; if (!parent) return; while (m.firstChild) parent.insertBefore(m.firstChild, m); parent.removeChild(m); if (parent.normalize) parent.normalize(); });
		return { ok: true, cleared: marks.length };
	} catch (e) { return { ok: false }; }
}

// --- Picture-in-Picture ---------------------------------------------------
// Toolbar action: pop the active page's video out (or exit PiP if already out).
// The panel-button click supplies the user gesture; same-origin proxied frames
// inherit that activation. The tab iframe carries allow="picture-in-picture".
// Is a video from the active page popped out right now?
export function pipOn() {
	const t = active();
	const win = t && frameWin(t.id);
	try { return !!(win && win.document.pictureInPictureElement); } catch { return false; }
}
export function pipActive() {
	const t = active();
	if (!isProxied(t)) return { ok: false, reason: "Open a page with a video first." };
	if (remoteOf(t)) { remoteOf(t).tool("pip"); return { ok: true }; }
	const win = frameWin(t.id);
	if (!win) return { ok: false };
	try {
		const doc = win.document;
		if (doc.pictureInPictureElement) { if (doc.exitPictureInPicture) doc.exitPictureInPicture().catch(() => {}); return { ok: true, pip: false }; }
		const vids = [...doc.querySelectorAll("video")];
		const v = vids.find((x) => !x.paused && x.readyState > 2) || vids.find((x) => x.readyState > 2) || vids[0];
		if (!v) return { ok: false, reason: "No video found on this page." };
		if (!v.requestPictureInPicture) return { ok: false, reason: "This browser doesn't support Picture-in-Picture." };
		v.requestPictureInPicture().catch(() => {});
		return { ok: true, pip: true };
	} catch (e) { return { ok: false, reason: "Picture-in-Picture was blocked on this page." }; }
}

// --- Read Aloud ------------------------------------------------------------
// The reader itself is a lazy chunk (app/read-aloud.js via app/read-gate.js); it reaches the page
// through these.
export const activePage = () => { const t = active(); return isProxied(t) ? t : null; };
export { frameWin, remoteOf };

// --- lifecycle ------------------------------------------------------------
// Apply the auto-injecting page tools to one freshly-loaded proxied frame.
export function applyExtFrame(id) {
	const t = byId(id);
	if (!isProxied(t)) return;
	applyDark(id, get("extDarkMode") === true);
	applyHighlighter(id, get("extHighlighter") === true);
	if (get("extDevtools") === true) enableDevtools(id);
}
// Re-apply to every live proxied frame after a toggle change.
function refreshAll() {
	for (const [id] of frames) {
		const t = byId(id);
		if (!isProxied(t)) continue;
		applyDark(id, get("extDarkMode") === true);
		applyHighlighter(id, get("extHighlighter") === true);
		if (get("extDevtools") === true) enableDevtools(id); else disableDevtools(id);
	}
}

let lastDev = get("extDevtools") === true;
let lastDark = get("extDarkMode") === true;
let lastHl = get("extHighlighter") === true;
export function initExtFrames() {
	subscribe(() => {
		const dev = get("extDevtools") === true;
		const dark = get("extDarkMode") === true;
		const hl = get("extHighlighter") === true;
		if (dev !== lastDev || dark !== lastDark || hl !== lastHl) { lastDev = dev; lastDark = dark; lastHl = hl; refreshAll(); }
	});
}
